import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormSelect,
  FormInput,
  Badge,
} from 'shards-react';
import { useDispatch } from 'react-redux';
import { warning } from 'react-toastify-redux';
import { v4 as uuidv4 } from 'uuid';
import {
  variantLabel,
  buildRowOptions,
  buyLabel,
  buildBundleLine,
  pushBundleLine,
} from './bundleItems';
import { getBundlePromotions } from '../../store/actions/promotions';

const BundleTable = styled.table`
  td,
  th {
    vertical-align: top;
  }
`;

const AddBundleModal = ({ open, onClose, onConfirm, products }) => {
  const dispatch = useDispatch();
  const [bundles, setBundles] = useState([]);
  const [selectedBundleId, setSelectedBundleId] = useState('');
  const [rows, setRows] = useState([]);
  // how many complete sets of the bundle to put on the order
  const [sets, setSets] = useState(1);
  const [loading, setLoading] = useState(false);

  const selectedBundle = bundles.find((b) => b.id === selectedBundleId) || null;

  useEffect(() => {
    let cancelled = false;
    if (open) {
      setSelectedBundleId('');
      setRows([]);
      setSets(1);
      setLoading(true);
      getBundlePromotions().then((items) => {
        // the modal can be closed again before the list lands
        if (!cancelled) {
          setBundles(items);
          setLoading(false);
        }
      });
    }
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleSelectBundle = (e) => {
    const { value } = e.target;
    setSelectedBundleId(value);
    const bundle = bundles.find((b) => b.id === value);
    setSets(1);
    if (!bundle) {
      setRows([]);
      return;
    }

    const nextRows = bundle.bundle.buys.map((buy) => {
      const options = buildRowOptions(buy, products);
      const first = options[0];
      return {
        label: buyLabel(buy),
        qty: buy.qty || 1,
        options,
        productId: first ? first.id : '',
        variantIndex: first && first.variants.length ? 0 : -1,
      };
    });
    setRows(nextRows);
  };

  const handleSelectProduct = (e, index) => {
    const { value } = e.target;
    const next = [...rows];
    next[index] = { ...next[index], productId: value, variantIndex: 0 };
    setRows(next);
  };

  const handleSelectVariant = (e, index) => {
    const { value } = e.target;
    const next = [...rows];
    next[index] = { ...next[index], variantIndex: parseInt(value, 10) };
    setRows(next);
  };

  const handleConfirm = () => {
    if (!selectedBundle) {
      dispatch(warning('Select a bundle first'));
      return;
    }

    const bundleGroupId = uuidv4();
    const lines = [];
    const lowStock = [];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const option = row.options.find((o) => o.id === row.productId);
      if (!option || row.variantIndex < 0) {
        dispatch(warning(`Select a product for "${row.label}"`));
        return;
      }
      const variant = option.variants[row.variantIndex];
      if (!variant) {
        dispatch(warning(`Select a variation for ${option.name}`));
        return;
      }
      const product = products.find((p) => p.id === option.id);
      if (!product) {
        dispatch(warning(`${option.name} is no longer in the catalogue`));
        return;
      }

      const lineQty = row.qty * sets;
      // stock counters are known to drift, so flag rather than block
      if (
        typeof variant.availableQty === 'number' &&
        variant.availableQty < lineQty
      )
        lowStock.push(`${product.name} (${variant.availableQty})`);

      pushBundleLine(
        lines,
        buildBundleLine(product, variant, lineQty, {
          bundleGroupId,
          bundleCode: selectedBundle.code,
        })
      );
    }

    if (!lines.length) {
      dispatch(warning('This bundle has no items to add'));
      return;
    }
    if (lowStock.length) dispatch(warning(`Low stock: ${lowStock.join(', ')}`));

    onConfirm(lines, selectedBundle.code);
  };

  return (
    <Modal open={open} toggle={onClose} size="lg">
      <ModalHeader>Add bundle</ModalHeader>
      <ModalBody>
        <div className="mb-3">
          <label>Bundle</label>
          <FormSelect value={selectedBundleId} onChange={handleSelectBundle}>
            <option value="">{loading ? 'Loading…' : 'Select a bundle'}</option>
            {bundles.map((b) => (
              <option key={b.id} value={b.id}>
                {b.code}
              </option>
            ))}
          </FormSelect>
          {!loading && !bundles.length && (
            <small className="text-muted">
              No active bundle promotions are running today.
            </small>
          )}
        </div>

        {rows.length > 0 && (
          <div className="mb-3" style={{ maxWidth: '160px' }}>
            <label>Sets</label>
            <FormInput
              type="number"
              min={1}
              value={sets}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                setSets(Number.isNaN(value) || value < 1 ? 1 : value);
              }}
            />
            <small className="text-muted">
              How many of this bundle the customer is buying.
            </small>
          </div>
        )}

        {rows.length > 0 && (
          <div className="table-responsive">
            <BundleTable className="table">
              <thead>
                <tr>
                  <th>Required</th>
                  <th width="35%">Product</th>
                  <th width="35%">Variation</th>
                  <th width="70">Qty</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  const option = row.options.find(
                    (o) => o.id === row.productId
                  );
                  return (
                    <tr key={index}>
                      <td>
                        <small>{row.label}</small>
                      </td>
                      <td>
                        {row.options.length ? (
                          <FormSelect
                            value={row.productId}
                            disabled={row.options.length === 1}
                            onChange={(e) => handleSelectProduct(e, index)}
                          >
                            {row.options.map((o) => (
                              <option key={o.id} value={o.id}>
                                {o.name}
                              </option>
                            ))}
                          </FormSelect>
                        ) : (
                          <Badge theme="danger">No matching product</Badge>
                        )}
                      </td>
                      <td>
                        {option && option.variants.length > 0 && (
                          <FormSelect
                            value={row.variantIndex}
                            disabled={option.variants.length === 1}
                            onChange={(e) => handleSelectVariant(e, index)}
                          >
                            {option.variants.map((v, vIndex) => (
                              <option key={vIndex} value={vIndex}>
                                {variantLabel(v)}
                              </option>
                            ))}
                          </FormSelect>
                        )}
                      </td>
                      <td>{row.qty * sets}</td>
                    </tr>
                  );
                })}
              </tbody>
            </BundleTable>
            <small className="text-muted">
              Adds these items to the order and applies {selectedBundle.code}
              {sets > 1 ? ` x ${sets} sets` : ''}.
            </small>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          type="button"
          theme="primary"
          disabled={!rows.length}
          onClick={handleConfirm}
        >
          Add to order
        </Button>
        <Button type="button" theme="secondary" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddBundleModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  products: PropTypes.array,
};

AddBundleModal.defaultProps = {
  onClose: () => {},
  onConfirm: () => {},
  products: [],
};

export default AddBundleModal;
